import { Controller } from "@hotwired/stimulus"

// Sauvegarde focus + caret + VALEUR entre 2 navigations Turbo pour que les inputs
// de recherche/ville ne perdent pas les lettres tapées pendant le debounce.
// Retour Sandrine (10/06/2026) : sur connexion lente, l'utilisateur tape pendant
// que la page se recharge — Turbo replace écrasait l'input avec la valeur soumise,
// les lettres tapées entre-temps étaient perdues. On restaure maintenant la valeur
// complète et on re-soumet si elle diffère de ce que le serveur a reçu.
const FOCUS_KEY = "filterFocus"

export default class extends Controller {
  static targets = ["form"]

  connect() {
    this.restoreFocus()
  }

  submit() {
    clearTimeout(this.debounceTimer)
    this.formTarget.requestSubmit()
  }

  submitDebounced(event) {
    clearTimeout(this.debounceTimer)
    // Sauvegarde à CHAQUE frappe (pas seulement au submit) : couvre les lettres
    // tapées pendant qu'un reload Turbo est en vol.
    this.rememberFocus(event?.target)
    this.debounceTimer = setTimeout(() => {
      this.rememberFocus(event?.target)
      this.formTarget.requestSubmit()
    }, 500)
  }

  // Sprint 1 (28/05/2026) — handler du sélecteur de mois supports B2C.
  // Le select émet "2026-3" ou "all". On parse + on update les hidden inputs
  // month/year/all avant de submit (pour que Rails reçoive les bons params).
  submitMonth(event) {
    const value = event.target.value
    const form = this.formTarget
    const monthInput = form.querySelector('input[name="month"]')
    const yearInput = form.querySelector('input[name="year"]')
    const allInput = form.querySelector('input[name="all"]')

    if (value === "all") {
      if (!allInput) {
        const inp = document.createElement("input")
        inp.type = "hidden"
        inp.name = "all"
        inp.value = "true"
        form.appendChild(inp)
      } else {
        allInput.value = "true"
      }
      if (monthInput) monthInput.disabled = true
      if (yearInput) yearInput.disabled = true
    } else {
      const [year, month] = value.split("-")
      if (yearInput) { yearInput.value = year; yearInput.disabled = false }
      if (monthInput) { monthInput.value = month; monthInput.disabled = false }
      if (allInput) allInput.remove()
    }
    form.requestSubmit()
  }

  disconnect() {
    clearTimeout(this.debounceTimer)
  }

  rememberFocus(input) {
    if (!input || !input.name) return
    try {
      sessionStorage.setItem(FOCUS_KEY, JSON.stringify({
        name: input.name,
        value: input.value ?? "",
        selectionEnd: input.selectionEnd ?? input.value?.length ?? 0,
        path: window.location.pathname,
      }))
    } catch (_) {}
  }

  restoreFocus() {
    let payload
    try {
      payload = sessionStorage.getItem(FOCUS_KEY)
      sessionStorage.removeItem(FOCUS_KEY)
    } catch (_) { return }
    if (!payload) return
    try {
      const { name, value, selectionEnd, path } = JSON.parse(payload)
      // Garde-fou cross-page : la frappe sauvegardée sur /accounts ne doit pas
      // être injectée dans le champ search d'une autre page (ex: /deals).
      if (path && path !== window.location.pathname) return
      const input = this.formTarget.querySelector(`[name="${name}"]`)
      if (!input) return
      // Restaure les lettres tapées pendant le reload : si la valeur live tapée
      // diffère de celle rendue par le serveur (= celle soumise), on ré-applique
      // la frappe de l'utilisateur et on re-soumet (debounced) pour converger.
      if (typeof value === "string" && value !== input.value) {
        input.value = value
        this.rememberFocus(input)
        clearTimeout(this.debounceTimer)
        this.debounceTimer = setTimeout(() => this.formTarget.requestSubmit(), 500)
      }
      input.focus()
      if (typeof input.setSelectionRange === "function") {
        const pos = Math.min(selectionEnd ?? input.value.length, input.value.length)
        input.setSelectionRange(pos, pos)
      }
    } catch (_) {}
  }
}
